import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Loader from '../../../shared/components/ui/Loader';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import GridWrapper from '../../../shared/components/layout/GridWrapper';
import Button from '../../../shared/components/ui/Button';
import Modal from '../../../shared/components/ui/Modal';
import FormInput from '../../../shared/components/ui/FormInput';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  getFollowUpsByProjectId,
  getEngineerPrioritiesByProjectId,
  getInventoryDailyTasks,
  updateFollowUp,
  updateTaskForTomorrow,
  updateInventoryDailyTasks,
  getManagementPriorities,
  approveUnderConsideration,
  approveActivity,
  getCustomerChangeRequestTasks,
  updateCustomerChangeRequeItem,
  getFileRequests,
  approveFileRequest,
  getAllUsersByProjectId,
  updateManagementPriorities,
  approveCancelInvoice,
  rejectCancelInvoice
} from '../../services/api/implementation/impl';
import { isProjectSetupReviewType } from '../../utils/projectStructurePriorities';
import {
  isCreateInventoryTaskType,
  isInventorySetupReviewType
} from '../../utils/inventoryTaskNavigation';
import Table from '../../../shared/components/layout/Table';
import { transformData } from '../../../super-admin/utils/transformData';
import noDataIcon from '../../../shared/assets/svg/NoData.svg';
import DP from '../../../shared/assets/svg/noUser.svg';
import LeftArrow from '../../../shared/assets/svg/left_arrow.svg';
import BackArrow from '../../../shared/assets/svg/backbtn.svg';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import {
  buildReturnToFromLocation,
  navigateWithReturn,
  useSmartBack
} from '../../../shared/navigation';
import AiSectionBanner from '../../components/ai/AiSectionBanner';

const Priorities = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { onBack } = useSmartBack({ fallback: '/builder/management' });
  const userID = Number(Cookies.get('userId'));
  const { collapsed } = useSidebar();
  const escalationType = location.state?.escalationType || null;
  const priorityType = location.state?.priorityType || null;
  const projectID = location.state?.projectID || null;
  const selectedDate = location.state?.selectedDate || null;

  // Modal states
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [approvingPriorityId, setApprovingPriorityId] = useState(null);

  // CCR states
  const [showCCRItems, setShowCCRItems] = useState(false);
  const [selectedCCRId, setSelectedCCRId] = useState(null);
  const [selectedPriorityId, setSelectedPriorityId] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm();

  // Fetch File Requests
  const {
    data: fileRequestsData,
    isLoading: isLoadingFileRequests,
    refetch: refetchFileRequests
  } = useQuery({
    queryKey: ['fileRequests'],
    queryFn: () => getFileRequests(),
    enabled: priorityType === 'File Request'
  });

  // Fetch users for resolving Lead Name (only if needed for File Request)
  const { data: projectUsersData } = useQuery({
    queryKey: ['projectUsers', projectID],
    queryFn: () => getAllUsersByProjectId(projectID, 1, 10000),
    enabled: priorityType === 'File Request' && !!projectID
  });

  // Create a map of userId -> userName for quick lookup
  const userMap = useMemo(() => {
    if (!projectUsersData?.data) return {};
    return projectUsersData.data.reduce((acc, user) => {
      acc[user.id] = user.name;
      return acc;
    }, {});
  }, [projectUsersData]);

  // Fetch data based on escalation type
  const {
    data: salesEscalationsData,
    refetch: refetchSalesEscalations,
    isLoading: isLoadingSalesEscalations
  } = useQuery({
    queryKey: ['salesEscalations', projectID],
    queryFn: () => getFollowUpsByProjectId(projectID, null),
    enabled: escalationType === 'salesEscalations' && !!projectID,
    onSuccess: (data) => {
      mixpanel.track('Sales Escalations Fetched', {
        user_id: userID,
        project_id: projectID,
        count: data?.data?.length || 0,
        timestamp: new Date().toISOString()
      });
    }
  });

  const {
    data: engineerEscalationsData,
    refetch: refetchEngineerEscalations,
    isLoading: isLoadingEngineerEscalations
  } = useQuery({
    queryKey: ['engineerEscalations', projectID, selectedDate],
    queryFn: () => getEngineerPrioritiesByProjectId(projectID, selectedDate),
    enabled: escalationType === 'engineerEscalations' && !!projectID && !!selectedDate,
    onSuccess: (data) => {
      mixpanel.track('Engineer Escalations Fetched', {
        user_id: userID,
        project_id: projectID,
        count: data?.data?.length || 0,
        timestamp: new Date().toISOString()
      });
    }
  });

  const {
    data: inventoryEscalationsData,
    refetch: refetchInventoryEscalations,
    isLoading: isLoadingInventoryEscalations
  } = useQuery({
    queryKey: ['inventoryEscalations', projectID, selectedDate],
    queryFn: () => getInventoryDailyTasks(projectID, selectedDate),
    enabled: escalationType === 'inventoryEscalations' && !!projectID && !!selectedDate,
    onSuccess: (data) => {
      mixpanel.track('Inventory Escalations Fetched', {
        user_id: userID,
        project_id: projectID,
        count: data?.length || 0,
        timestamp: new Date().toISOString()
      });
    }
  });

  // Fetch management priorities for approval types (e.g., Approve UC)
  const {
    data: managementPrioritiesData,
    refetch: refetchManagementPriorities,
    isLoading: isLoadingManagementPriorities
  } = useQuery({
    queryKey: ['managementPriorities', projectID, priorityType],
    queryFn: () => getManagementPriorities(projectID, priorityType),
    enabled: !!priorityType && !!projectID && !showCCRItems,
    onSuccess: (data) => {
      mixpanel.track('Management Priorities Fetched', {
        user_id: userID,
        project_id: projectID,
        priority_type: priorityType,
        count: data?.data?.length || 0,
        timestamp: new Date().toISOString()
      });
    }
  });

  // Fetch CCR tasks when CCR is selected
  const {
    data: ccrTasksData,
    refetch: refetchCCRTasks,
    isLoading: isLoadingCCRTasks
  } = useQuery({
    queryKey: ['ccrTasks', selectedCCRId],
    queryFn: () => getCustomerChangeRequestTasks(selectedCCRId, 1, 10000),
    enabled: !!selectedCCRId && showCCRItems,
    onSuccess: (data) => {
      mixpanel.track('CCR Tasks Fetched', {
        user_id: userID,
        ccr_id: selectedCCRId,
        count: data?.data?.length || 0,
        timestamp: new Date().toISOString()
      });
    }
  });

  // Process and filter data based on escalation type or priority type
  const processedData = useMemo(() => {
    if (escalationType === 'salesEscalations' && salesEscalationsData?.data) {
      return salesEscalationsData.data.filter((item) => {
        if (!(item.isEscalated && item.status === 'Active')) return false;
        const leadStatus = item?.lead?.status;
        const leadPstatus = item?.lead?.pstatus;
        const stage = item?.stage || item?.lead?.pstatus;
        // Closed Won / closed deals must not appear in escalations
        if (leadStatus === 'Closed') return false;
        if (leadPstatus === 'Closed Won') return false;
        if (stage === 'Deal Closed' || stage === 'Closed Won') return false;
        return true;
      });
    }
    if (escalationType === 'engineerEscalations' && engineerEscalationsData?.data) {
      return engineerEscalationsData.data.filter(
        (item) => item.isEscalated && item.status !== 'Completed'
      );
    }
    if (escalationType === 'inventoryEscalations' && inventoryEscalationsData) {
      return inventoryEscalationsData.filter((item) => item.status !== 'Completed');
    }
    if (priorityType === 'File Request' && fileRequestsData?.data) {
      if (projectID) {
        return fileRequestsData.data.filter(item => item.project_id === Number(projectID));
      }
      return fileRequestsData.data;
    }
    if (priorityType && managementPrioritiesData?.data) {
      // Filter out completed priorities (and already-approved deals whose priority row lagged)
      return managementPrioritiesData.data.filter((item) => {
        if (item.status === 'Completed') return false;
        if (
          priorityType === 'Deal Approval' &&
          (item?.deal?.status === 'Approved' ||
            String(item?.deal?.status || '').toLowerCase() === 'approved')
        ) {
          return false;
        }
        // Invoice cancel: hide when invoice already cancelled even if cache lag kept status Pending
        if (
          priorityType === 'Invoice Cancel' &&
          (item?.invoice?.cancelled ||
            item?.invoice?.status === 'Cancelled' ||
            String(item?.invoice?.status || '').toLowerCase() === 'cancelled')
        ) {
          return false;
        }
        return true;
      });
    }
    return [];
  }, [
    escalationType,
    priorityType,
    salesEscalationsData,
    engineerEscalationsData,
    inventoryEscalationsData,
    managementPrioritiesData,
    fileRequestsData,
    projectID
  ]);

  // Format date helper
  function formatDate(dateString) {
    if (!dateString) return '---';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '---';
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('en-GB', options);
  }

  // Transform data for management priorities (Approve UC, Deal Approval, Approve CCR, etc.)
  const handleManagementPriorityData = (item) => {
    if (priorityType === 'File Request') {
      const leadUserId = item?.lead?.user_id;
      const leadName = leadUserId ? userMap[leadUserId] || 'Unknown User' : '---';

      return {
        id: item?.id,
        file_name: item?.file_name || '---',
        lead_name: leadName,
        project: item?.project?.name || '---',
        status: item?.status ? 'Approved' : 'Pending',
        created_at: item?.created_at ? item.created_at : '---',
        file_url: item?.file_url
      };
    }
    if (priorityType === 'Approve CCR') {
      return {
        id: item?.id,
        Customer_Name: item?.ccr?.deal?.lead?.user?.name || '---',
        Unit_Block: `${item?.ccr?.deal?.unit?.unitName || 'N/A'}/${item?.ccr?.deal?.unit?.block?.name || 'N/A'}`,
        Project_Name: item?.ccr?.deal?.unit?.project?.name || '---',
        No_of_Requests: item?.ccr?.total_requirements || 0,
        Total_Cost:
          ((item?.ccr?.builderStatus === 'Accepted' || item?.ccr?.engineerStatus === 'Accepted') &&
            item?.ccr?.budget) ||
          0,
        Total_Effort: `${((item?.ccr?.builderStatus === 'Accepted' || item?.ccr?.engineerStatus === 'Accepted') &&
          item?.ccr?.totalDays) ||
          '---'
          } Days`,
        status: item?.ccr?.globalStatus || item?.status || '---',
        Customer_Approval:
          item?.ccr?.globalStatus === 'Approved'
            ? 'Pending'
            : item?.ccr?.globalStatus === 'Accepted'
              ? 'Accepted'
              : 'N/A',
        created_at: item.createdAt ? item.createdAt : '---'
      };
    }
    if (priorityType === 'Invoice Cancel') {
      const isCompleted =
        item.status === 'Completed' ||
        item?.invoice?.cancelled ||
        item?.invoice?.status === 'Cancelled';
      const invoice = item?.invoice;
      const deal = invoice?.closedDeal || item?.deal;
      // Late-fee rows often hold value in late_fees; amount may be NaN/"NaN".
      // Pass a raw number — transformData formats currency (double-format caused ₹NaN).
      const amountRaw = Number(invoice?.amount);
      const lateRaw = Number(
        invoice?.late_fees ?? invoice?.lateFees ?? invoice?.late_fee
      );
      const amount = Number.isFinite(amountRaw) ? amountRaw : 0;
      const late = Number.isFinite(lateRaw) ? lateRaw : 0;
      const displayAmount = Math.max(0, amount + late);
      return {
        id: item?.id,
        invoice_number: invoice?.invoiceNumber || `Invoice #${item?.invoice_id || '---'}`,
        name: deal?.lead?.user?.name || '---',
        Unit_Name: deal?.unit?.unitName || '---',
        project: item?.project?.name || '---',
        amount: displayAmount,
        source: invoice?.source || '---',
        status: isCompleted ? 'Cancelled' : 'Pending',
        created_at: item.createdAt || item.created_at || '---',
        actions: isCompleted ? '---' : 'Approval'
      };
    }
    if (priorityType === 'Approve UC') {
      const isCompleted = item.status === 'Completed' || item.uc?.approved;
      return {
        id: item?.id,
        name: item?.uc?.lead?.user?.name || '---',
        Unit_Name: item?.uc?.unit?.unitName || '---',
        project: item?.project?.name || '---',
        Customer_Proposal:
          item.uc?.customer_proposal !== 0 &&
            item.uc?.customer_proposal !== null &&
            item.uc?.customer_proposal !== undefined &&
            item.uc?.customer_proposal !== '' &&
            !isNaN(item.uc.customer_proposal)
            ? `₹${parseInt(item.uc.customer_proposal).toLocaleString('en-IN')}`
            : '₹0',
        Plc_Price:
          item.uc?.plc_price !== 0 &&
            item.uc?.plc_price !== null &&
            item.uc?.plc_price !== undefined &&
            item.uc?.plc_price !== '' &&
            !isNaN(item.uc.plc_price)
            ? `₹${parseInt(item.uc.plc_price).toLocaleString('en-IN')}`
            : '₹0',
        GST:
          item.uc?.gst !== 0 &&
            item.uc?.gst !== null &&
            item.uc?.gst !== undefined &&
            item.uc?.gst !== '' &&
            !isNaN(item.uc.gst)
            ? `₹${parseInt(item.uc.gst).toLocaleString('en-IN')}`
            : '₹0',
        Listed_Rate: `₹${(parseInt(item.uc?.plc_price) || 0) +
          (parseInt(item.uc?.extra_charges) || 0) +
          (parseInt(item.uc?.gst) || 0) +
          (parseInt(item.uc?.customer_proposal) || 0)
          }`,
        Customer_Discount:
          item.uc?.discount !== 0 &&
            item.uc?.discount !== null &&
            item.uc?.discount !== undefined &&
            item.uc?.discount !== '' &&
            !isNaN(item.uc.discount)
            ? `₹${parseInt(item.uc.discount).toLocaleString('en-IN')}`
            : '₹0',
        // Status must not be the string "Approval" — Table treats that as the Action button.
        status: isCompleted ? 'Approved' : 'Pending',
        created_at: item.createdAt || item.created_at || '---',
        actions: isCompleted ? '---' : 'Approval'
      };
    }
    if (priorityType === 'Deal Approval') {
      const isCompleted =
        item.status === 'Completed' || item?.deal?.status === 'Approved';
      return {
        id: item?.id,
        name: item?.deal?.lead?.user?.name || '---',
        Unit_Name: item?.deal?.unit?.unitName || '---',
        project: item?.project?.name || '---',
        Customer_Proposal:
          item.deal?.unit_price !== 0 &&
            item.deal?.unit_price !== null &&
            item.deal?.unit_price !== undefined &&
            item.deal?.unit_price !== '' &&
            !isNaN(item.deal.unit_price)
            ? `₹${parseInt(item.deal.unit_price).toLocaleString('en-IN')}`
            : '₹0',
        Plc_Price:
          item.deal?.plc_price !== 0 &&
            item.deal?.plc_price !== null &&
            item.deal?.plc_price !== undefined &&
            item.deal?.plc_price !== '' &&
            !isNaN(item.deal.plc_price)
            ? `₹${parseInt(item.deal.plc_price).toLocaleString('en-IN')}`
            : '₹0',
        GST:
          item.deal?.gst_price !== 0 &&
            item.deal?.gst_price !== null &&
            item.deal?.gst_price !== undefined &&
            item.deal?.gst_price !== '' &&
            !isNaN(item.deal.gst_price)
            ? `₹${parseInt(item.deal.gst_price).toLocaleString('en-IN')}`
            : '₹0',
        Listed_Rate: `₹${(parseInt(item.deal?.unit_price) || 0) +
          (parseInt(item.deal?.extra_charges) || 0) +
          (parseInt(item.deal?.gst_price) || 0)
          }`,
        Customer_Discount:
          item.deal?.discount !== 0 &&
            item.deal?.discount !== null &&
            item.deal?.discount !== undefined &&
            item.deal?.discount !== '' &&
            !isNaN(item.deal.discount)
            ? `₹${parseInt(item.deal.discount).toLocaleString('en-IN')}`
            : '₹0',
        // Status must not be the string "Approval" — Table treats that as the Action button.
        status: isCompleted ? 'Approved' : 'Pending',
        created_at: item.createdAt ? item.createdAt : '---',
        lead_id: item?.deal?.lead_id || item?.deal?.lead?.id || null,
        actions: isCompleted ? '---' : 'Approval'
      };
    }
    // Default structure for other priority types (e.g. Pricing Update)
    const isCompleted = item.status === 'Completed';
    return {
      id: item?.id,
      name: item?.title || '---',
      description: item?.description || '---',
      project: item?.project?.name || '---',
      // Keep status as Pending/Completed — "Approval" is reserved for the Action column cell.
      status: isCompleted ? 'Completed' : 'Pending',
      created_at: item.createdAt || item.created_at || '---',
      actions: isCompleted ? '---' : 'Approval'
    };
  };

  // Transform data based on escalation type or priority type
  const tableData = useMemo(() => {
    if (priorityType && processedData.length > 0) {
      if (priorityType === 'File Request') {
        return processedData.map((item) =>
          transformData(handleManagementPriorityData(item), [
            'id',
            'file_name',
            'lead_name',
            'project',
            'status',
            'created_at',
            'file_url'
          ])
        );
      }
      if (priorityType === 'Approve UC' || priorityType === 'Deal Approval') {
        return processedData.map((item) =>
          transformData(handleManagementPriorityData(item), [
            'id',
            'name',
            'Unit_Name',
            'project',
            'Customer_Proposal',
            'Plc_Price',
            'GST',
            'Listed_Rate',
            'Customer_Discount',
            'status',
            'created_at',
            ...(priorityType === 'Approve UC' ? ['actions'] : []),
            ...(priorityType === 'Deal Approval' ? ['lead_id'] : [])
          ])
        );
      }
      if (priorityType === 'Invoice Cancel') {
        return processedData.map((item) =>
          transformData(handleManagementPriorityData(item), [
            'id',
            'invoice_number',
            'name',
            'Unit_Name',
            'project',
            'amount',
            'source',
            'status',
            'created_at',
            'actions'
          ])
        );
      }
      if (priorityType === 'Approve CCR') {
        return processedData.map((item) =>
          transformData(handleManagementPriorityData(item), [
            'id',
            'Customer_Name',
            'Unit_Block',
            'Project_Name',
            'No_of_Requests',
            'Total_Cost',
            'Total_Effort',
            'status',
            'Customer_Approval',
            'created_at'
          ])
        );
      }
      // For other priority types, use a simpler format (includes Action column)
      return processedData.map((item) =>
        transformData(handleManagementPriorityData(item), [
          'id',
          'name',
          'description',
          'project',
          'status',
          'created_at',
          'actions'
        ])
      );
    }
    if (escalationType === 'salesEscalations') {
      const leadsFormat = [
        'name',
        'phone_number',
        'project',
        'agenda',
        'follow_up_date',
        'type',
        'assigned_to',
        'actions',
        'id',
        'lead_id'
      ];
      return processedData.map((el) =>
        transformData(
          {
            ...el.user,
            name: el?.lead?.user?.name,
            phone_number: el?.lead?.user?.phone_number,
            project: el?.project?.name || '---',
            agenda: el?.agenda || '---',
            follow_up_date: formatDate(el?.nextFollowUpDate) || '---',
            assigned_to: el?.user?.name,
            actions: `Actions---${el.id}`,
            id: el?.id,
            lead_id: el?.lead_id,
            type: el?.type
          },
          leadsFormat
        )
      );
    }
    if (escalationType === 'engineerEscalations') {
      const taskFormat = [
        'name',
        'block',
        'unit',
        'status',
        'type',
        'date',
        'assigned_to',
        'actions',
        'id'
      ];
      return processedData.map((el) =>
        transformData(
          {
            name: el?.liveTask?.name || '---',
            block: el?.liveTask?.block?.name || '---',
            unit: el?.liveTask?.unit?.unitName || '---',
            status: el?.status || '---',
            type: el?.type || '---',
            date: formatDate(el?.date) || '---',
            assigned_to: el?.user?.name || '---',
            actions: `Actions---${el.id}`,
            id: el?.id
          },
          taskFormat
        )
      );
    }
    if (escalationType === 'inventoryEscalations') {
      return processedData.map((el) => ({
        name: el?.name || '---',
        type: el?.type || '---',
        date: formatDate(el?.date) || '---',
        status: el?.status || '---',
        assigned_to: el?.user?.name || '---',
        actions: `Actions---${el.id}`,
        id: el?.id
      }));
    }
    return [];
  }, [processedData, escalationType, priorityType, userMap]);

  // Get table headers based on escalation type
  const getTableHeaders = () => {
    if (priorityType === 'File Request') {
      return (
        <>
          <th className="px-4 py-2 border-b font-normal">File Name</th>
          <th className="px-4 py-2 border-b font-normal">Lead Name</th>
          <th className="px-4 py-2 border-b font-normal">Project</th>
          <th className="px-4 py-2 border-b font-normal">Status</th>
          <th className="px-4 py-2 border-b font-normal">Created At</th>
          <th className="px-4 py-2 border-b font-normal">Action</th>
        </>
      );
    }
    if (escalationType === 'salesEscalations') {
      return (
        <>
          <th className="px-4 py-2 border-b font-normal">Lead Name</th>
          <th className="px-4 py-2 border-b font-normal">Phone</th>
          <th className="px-4 py-2 border-b font-normal">Project</th>
          <th className="px-4 py-2 border-b font-normal">Follow Up Agenda</th>
          <th className="px-4 py-2 border-b font-normal">Follow Up Date</th>
          <th className="px-4 py-2 border-b font-normal">Type</th>
          <th className="px-4 py-2 border-b font-normal">Assigned</th>
          <th className="px-4 py-2 border-b font-normal">Actions</th>
        </>
      );
    }
    if (escalationType === 'engineerEscalations') {
      return (
        <>
          <th className="px-4 py-2 border-b font-normal">Task Name</th>
          <th className="px-4 py-2 border-b font-normal">Block</th>
          <th className="px-4 py-2 border-b font-normal">Unit</th>
          <th className="px-4 py-2 border-b font-normal">Status</th>
          <th className="px-4 py-2 border-b font-normal">Type</th>
          <th className="px-4 py-2 border-b font-normal">Date</th>
          <th className="px-4 py-2 border-b font-normal">Assigned To</th>
          <th className="px-4 py-2 border-b font-normal">Actions</th>
        </>
      );
    }
    if (escalationType === 'inventoryEscalations') {
      return (
        <>
          <th className="px-4 py-2 border-b font-normal">Task Name</th>
          <th className="px-4 py-2 border-b font-normal">Type</th>
          <th className="px-4 py-2 border-b font-normal">Date</th>
          <th className="px-4 py-2 border-b font-normal">Status</th>
          <th className="px-4 py-2 border-b font-normal">Assigned To</th>
          <th className="px-4 py-2 border-b font-normal">Actions</th>
        </>
      );
    }
    return null;
  };

  // Get table row cells based on escalation type
  const getTableRowCells = (row) => {
    if (priorityType === 'File Request') {
      return (
        <>
          <td className="px-4 py-2">{row.file_name}</td>
          <td className="px-4 py-2">{row.lead_name}</td>
          <td className="px-4 py-2">{row.project}</td>
          <td className="px-4 py-2">{row.status}</td>
          <td className="px-4 py-2">{formatDate(row.created_at)}</td>
          <td className="px-4 py-2">
            {row.file_url ? (
              <a
                href={row.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                View File
              </a>
            ) : '---'}
            {row.status === 'Pending' && (
              <div className="ml-2 inline-block">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    approveFileRequestMutation.mutate({ id: row.id, payload: { status: true } });
                  }}
                  disabled={approveFileRequestMutation.isPending}
                >
                  Approve
                </Button>
              </div>
            )}
          </td>
        </>
      );
    }
    if (escalationType === 'salesEscalations') {
      return (
        <>
          <td className="px-4 py-2">{row.name}</td>
          <td className="px-4 py-2">{row.phone_number}</td>
          <td className="px-4 py-2">{row.project}</td>
          <td className="px-4 py-2">{row.agenda || '---'}</td>
          <td className="px-4 py-2">{row.follow_up_date}</td>
          <td className="px-4 py-2">{row.type}</td>
          <td className="px-4 py-2">
            <div className="flex items-center justify-center w-full relative">
              {row.assigned_to ? (
                <div className="flex cursor-pointer gap-1 justify-start items-center w-full">
                  <div className="w-6 h-6 rounded-full bg-[#FFB346] text-white flex items-center justify-center text-[0.8rem] font-semibold border-2 border-white leading-none">
                    {row.assigned_to?.charAt(0).toUpperCase()}
                  </div>
                  {row.assigned_to}
                </div>
              ) : (
                <img src={DP} alt="" className="cursor-pointer w-6 h-6 rounded-full" />
              )}
            </div>
          </td>
          <td className="px-4 py-2">
            <Button
              onClick={(e) => {
                e.stopPropagation();
                const fullItem = processedData.find((item) => item.id === row.id);
                setSelectedItem(fullItem);
                setShowNoteModal(true);
              }}>
              Add Note
            </Button>
          </td>
        </>
      );
    }
    if (escalationType === 'engineerEscalations') {
      return (
        <>
          <td className="px-4 py-2">{row.name}</td>
          <td className="px-4 py-2">{row.block}</td>
          <td className="px-4 py-2">{row.unit}</td>
          <td className="px-4 py-2">
            <div className="relative z-0">
              <span className="flex pl-6 overflow-hidden text-sm">{row.status}</span>
              <div
                className={`w-[5px] h-[100%] rounded-t-2xl rounded-r-[3rem] absolute left-[4px] top-[-2px] ${row.status === 'Delayed'
                  ? 'bg-[#C2281B]'
                  : row.status === 'Completed'
                    ? 'bg-[#3F8D78]'
                    : row.status === 'In Progress'
                      ? 'bg-[#3B82F6]'
                      : 'bg-gray-400'
                  }`}>
                &nbsp;
              </div>
            </div>
          </td>
          <td className="px-4 py-2">{row.type}</td>
          <td className="px-4 py-2">{row.date}</td>
          <td className="px-4 py-2">{row.assigned_to}</td>
          <td className="px-4 py-2">
            <Button
              onClick={(e) => {
                e.stopPropagation();
                const fullItem = processedData.find((item) => item.id === row.id);
                setSelectedItem(fullItem);
                setShowNoteModal(true);
              }}>
              Add Note
            </Button>
          </td>
        </>
      );
    }
    if (escalationType === 'inventoryEscalations') {
      const isAddVendorsTask = row.type === 'Add Vendors';
      const isCreateInventoryTask = isCreateInventoryTaskType(row.type);
      const fullItem = processedData.find((item) => item.id === row.id);
      return (
        <>
          <td className="px-4 py-2">{row.name}</td>
          <td className="px-4 py-2">{row.type}</td>
          <td className="px-4 py-2">{row.date}</td>
          <td className="px-4 py-2">{row.status}</td>
          <td className="px-4 py-2">{row.assigned_to}</td>
          <td className="px-4 py-2">
            {isAddVendorsTask ? (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  navigateWithReturn(navigate, '/builder/inventory/vendors', {
                    returnTo: buildReturnToFromLocation(location)
                  });
                }}>
                Add Vendors
              </Button>
            ) : isCreateInventoryTask ? (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  navigateWithReturn(navigate, '/builder/inventory/stock', {
                    returnTo: buildReturnToFromLocation(location),
                    state: {
                      projectID: fullItem?.project_id || projectID
                    }
                  });
                }}>
                Open Stock
              </Button>
            ) : (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  const fullItem = processedData.find((item) => item.id === row.id);
                  setSelectedItem(fullItem);
                  setShowNoteModal(true);
                }}>
                Add Note
              </Button>
            )}
          </td>
        </>
      );
    }
    return null;
  };

  // Get page title
  const getPageTitle = () => {
    if (escalationType === 'salesEscalations') return 'Sales Escalations';
    if (escalationType === 'engineerEscalations') return 'Engineer Escalations';
    if (escalationType === 'inventoryEscalations') return 'Inventory Escalations';
    if (priorityType) return priorityType;
    return 'Priorities';
  };

  // Update mutations
  const updateFollowUpMutation = useMutation({
    mutationFn: ({ id, payload }) => updateFollowUp(id, payload),
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to update follow-up');
    }
  });

  const updateEngineerTaskMutation = useMutation({
    mutationFn: ({ id, payload }) => updateTaskForTomorrow(id, payload),
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to update task');
    }
  });

  const updateInventoryTaskMutation = useMutation({
    mutationFn: ({ id, payload }) => updateInventoryDailyTasks(id, payload),
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to update task');
    }
  });

  // Mutation for approving UC (same as UnderConsiderationList)
  const approveUCMutation = useMutation({
    mutationFn: ({ id, payload }) => approveUnderConsideration(id, payload),
    onSuccess: (_data, variables) => {
      toast.success('Approved Successfully');
      const priorityId = variables?.payload?.priority_id;
      if (priorityId != null) {
        queryClient.setQueryData(
          ['managementPriorities', projectID, priorityType],
          (prev) => {
            if (!prev?.data) return prev;
            return {
              ...prev,
              data: prev.data.filter((item) => Number(item.id) !== Number(priorityId))
            };
          }
        );
      }
      setApprovingPriorityId(null);
      refetchManagementPriorities();
    },
    onError: (error) => {
      setApprovingPriorityId(null);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to approve');
    }
  });

  // Mutation for approving Deal (same as DealCard)
  const approveDealMutation = useMutation({
    mutationFn: ({ id, payload }) => approveActivity(id, payload),
    onMutate: async (variables) => {
      const priorityId = variables?.payload?.priority_id;
      if (priorityId == null) return;
      setApprovingPriorityId(priorityId);
      await queryClient.cancelQueries({
        queryKey: ['managementPriorities', projectID, priorityType]
      });
      const previous = queryClient.getQueryData([
        'managementPriorities',
        projectID,
        priorityType
      ]);
      // Remove immediately so Approve reflects without waiting on API/cache
      queryClient.setQueryData(
        ['managementPriorities', projectID, priorityType],
        (prev) => {
          if (!prev?.data) return prev;
          return {
            ...prev,
            data: prev.data.filter((item) => Number(item.id) !== Number(priorityId))
          };
        }
      );
      return { previous };
    },
    onSuccess: () => {
      toast.success('Approved Successfully');
      setApprovingPriorityId(null);
      refetchManagementPriorities();
    },
    onError: (error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          ['managementPriorities', projectID, priorityType],
          context.previous
        );
      }
      setApprovingPriorityId(null);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to approve');
    }
  });

  const approveProjectSetupReviewMutation = useMutation({
    mutationFn: ({ id, payload }) => updateManagementPriorities(id, payload),
    onSuccess: () => {
      toast.success('Priority completed');
      refetchManagementPriorities();
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to complete priority'
      );
    }
  });

  // Mutation for approving File Request
  const approveFileRequestMutation = useMutation({
    mutationFn: ({ id, payload }) => approveFileRequest(id, payload),
    onSuccess: () => {
      toast.success('File Request Approved Successfully');
      refetchFileRequests();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to approve');
    }
  });

  const approveInvoiceCancelMutation = useMutation({
    mutationFn: async ({ id, payload, priorityId }) => {
      // 1) Cancel invoice + (BE) close priority
      const data = await approveCancelInvoice(id, payload);
      // 2) Belt-and-suspenders: mark management priority Completed so list never sticks
      if (priorityId != null) {
        try {
          await updateManagementPriorities(priorityId, {
            status: 'Completed',
            completedOnTime: true
          });
        } catch (e) {
          console.warn('Invoice cancel priority complete fallback failed', e);
        }
      }
      return data;
    },
    onSuccess: (_data, variables) => {
      toast.success('Invoice cancelled successfully');
      const priorityId = variables?.priorityId ?? variables?.payload?.priority_id;
      if (priorityId != null) {
        queryClient.setQueryData(
          ['managementPriorities', projectID, priorityType],
          (prev) => {
            if (!prev?.data) return prev;
            return {
              ...prev,
              data: prev.data.filter((item) => Number(item.id) !== Number(priorityId))
            };
          }
        );
      }
      // Drop cached list so refresh cannot resurrect Pending after approve
      queryClient.invalidateQueries({
        queryKey: ['managementPriorities', projectID, priorityType]
      });
      refetchManagementPriorities();
    },
    onError: (error) => {
      const status = error?.response?.status;
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to approve invoice cancel';
      toast.error(
        status === 403 ? `Access denied (${status}): ${msg}` : msg
      );
    }
  });

  const rejectInvoiceCancelMutation = useMutation({
    mutationFn: async ({ id, payload, priorityId }) => {
      const data = await rejectCancelInvoice(id, payload);
      if (priorityId != null) {
        try {
          await updateManagementPriorities(priorityId, {
            status: 'Completed',
            completedOnTime: true
          });
        } catch (e) {
          console.warn('Invoice cancel priority reject-complete fallback failed', e);
        }
      }
      return data;
    },
    onSuccess: (_data, variables) => {
      toast.success('Invoice cancel request rejected');
      const priorityId = variables?.priorityId ?? variables?.payload?.priority_id;
      if (priorityId != null) {
        queryClient.setQueryData(
          ['managementPriorities', projectID, priorityType],
          (prev) => {
            if (!prev?.data) return prev;
            return {
              ...prev,
              data: prev.data.filter((item) => Number(item.id) !== Number(priorityId))
            };
          }
        );
      }
      queryClient.invalidateQueries({
        queryKey: ['managementPriorities', projectID, priorityType]
      });
      refetchManagementPriorities();
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to reject invoice cancel'
      );
    }
  });

  // Mutation for approving CCR items
  const updateCCRItemMutation = useMutation({
    mutationFn: ({ id, payload }) => updateCustomerChangeRequeItem(id, payload),
    onSuccess: () => {
      toast.success('Item updated successfully');
      refetchCCRTasks();
      refetchManagementPriorities();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to update item');
    }
  });

  // Handle CCR item approval/rejection
  const handleCCRYes = (itemId) => {
    const payload = {
      builderStatus: 'Accepted',
      priority_id: selectedPriorityId
    };
    updateCCRItemMutation.mutate({ id: itemId, payload });
  };

  const handleCCRNo = (itemId) => {
    const payload = {
      builderStatus: 'Rejected',
      priority_id: selectedPriorityId
    };
    updateCCRItemMutation.mutate({ id: itemId, payload });
  };

  // Handle clicking on CCR priority to show items
  const handleCCRClick = (priorityItem) => {
    const ccrId = priorityItem.ccr_id;
    if (!ccrId) {
      toast.error('CCR ID not found');
      return;
    }
    setSelectedCCRId(ccrId);
    setSelectedPriorityId(priorityItem.id);
    setShowCCRItems(true);
  };

  // Format CCR items data similar to ChangeRequestItems
  const formatCCRItemData = (item) => {
    return {
      id: item.id,
      Requirements: item.title || '---',
      Assigned_to: item.customerChangeRequest?.engineer?.name || 'N/A',
      Effort: `${((item.builderStatus === 'Accepted' || item.engineerStatus === 'Accepted') &&
        item.noOfDays) ||
        '---'
        } Days`,
      Budget:
        ((item.builderStatus === 'Accepted' || item.engineerStatus === 'Accepted') &&
          item.budget) ||
        0,
      Status: item.globalStatus || '---',
      EApproval: item.customerChangeRequest?.engineer?.name ? item.engineerStatus : 'N/A',
      approval:
        ['Ongoing', 'Pending'].includes(item.engineerStatus) &&
          ['Ongoing', 'Pending'].includes(item.builderStatus)
          ? 'YesNoOption'
          : item.engineerStatus === 'Accepted' &&
            ['Ongoing', 'Pending'].includes(item.builderStatus)
            ? 'YesNoOption'
            : ['Ongoing', 'Pending'].includes(item.engineerStatus) &&
              item.builderStatus === 'Accepted'
              ? 'Accepted'
              : item.engineerStatus === 'Rejected' &&
                ['Ongoing', 'Pending'].includes(item.builderStatus)
                ? 'YesNoOption'
                : ['Ongoing', 'Pending'].includes(item.engineerStatus) &&
                  item.builderStatus === 'Rejected'
                  ? 'Rejected'
                  : item.engineerStatus === 'Rejected' && item.builderStatus === 'Rejected'
                    ? 'Rejected'
                    : item.builderStatus || '---'
    };
  };

  // CCR items table data
  const ccrItemsTableData = useMemo(() => {
    if (!ccrTasksData?.data) return [];
    return ccrTasksData.data.map((item) =>
      transformData(formatCCRItemData(item), [
        'id',
        'Requirements',
        'Assigned_to',
        'Effort',
        'Budget',
        'Status',
        'EApproval',
        'approval'
      ])
    );
  }, [ccrTasksData]);

  // Handle approve for management priorities (Approve UC or Deal Approval)
  const handleApprove = (id) => {
    // Find the priority item to get deal_id/uc_id and priority_id
    const priorityItem = managementPrioritiesData?.data?.find(
      (item) => Number(item.id) === Number(id)
    );

    if (!priorityItem) {
      toast.error('Item not found');
      return;
    }

    if (priorityType === 'Approve UC') {
      // Check if already approved by checking the UC status
      if (priorityItem.uc?.approved) {
        toast.error('Already Approved');
        return;
      }

      // Get the UC id from the priority item
      const ucId = priorityItem.uc_id;
      if (!ucId) {
        toast.error('UC ID not found');
        return;
      }

      // Payload with approved: true and priority_id
      const payload = {
        approved: true,
        priority_id: priorityItem.id
      };

      approveUCMutation.mutate({ id: ucId, payload });
    } else if (priorityType === 'Deal Approval') {
      // Check if already approved by checking the deal status
      if (
        priorityItem.deal?.status === 'Approved' ||
        priorityItem.status === 'Completed'
      ) {
        toast.error('Already Approved');
        return;
      }

      // Only use the Management Approval activity id — never fall back to deal_id
      // (PUT /activity/{deal_id} can approve the deal without completing the priority).
      const activityId = priorityItem.managementApprovalActivityId;
      if (!activityId) {
        toast.error(
          'Management Approval activity not found for this deal. Refresh and try again.'
        );
        return;
      }

      // Get lead_id from the deal
      const leadId = priorityItem.deal?.lead_id;
      if (!leadId) {
        toast.error('Lead ID not found');
        return;
      }

      // Payload with status: 'Completed', name: 'Management Approval', lead_id, and priority_id
      const payload = {
        name: 'Management Approval',
        lead_id: leadId,
        status: 'Completed',
        priority_id: priorityItem.id
      };

      approveDealMutation.mutate({ id: activityId, payload });
    } else if (priorityType === 'Invoice Cancel') {
      if (priorityItem.status === 'Completed' || priorityItem.invoice?.cancelled) {
        toast.error('Already Cancelled');
        return;
      }
      const invoiceId = priorityItem.invoice_id || priorityItem.invoice?.id;
      if (!invoiceId) {
        toast.error('Invoice ID not found');
        return;
      }
      approveInvoiceCancelMutation.mutate({
        id: invoiceId,
        payload: { priority_id: priorityItem.id },
        priorityId: priorityItem.id
      });
    } else if (
      isProjectSetupReviewType(priorityType) ||
      isInventorySetupReviewType(priorityType)
    ) {
      if (priorityItem.status === 'Completed') {
        toast.error('Already Approved');
        return;
      }
      approveProjectSetupReviewMutation.mutate({
        id: priorityItem.id,
        payload: { status: 'Completed' }
      });
    } else if (
      priorityType === 'Pricing Update' ||
      priorityType === 'PLC Pricing Update'
    ) {
      // Pricing updates require resetting booking targets for the project.
      if (priorityItem.status === 'Completed') {
        toast.error('Already completed');
        return;
      }
      const targetProjectId = priorityItem.project_id || projectID;
      if (!targetProjectId) {
        toast.error('Project not found for this priority');
        return;
      }
      navigateWithReturn(navigate, '/builder/management/set-target', {
        returnTo: buildReturnToFromLocation(location),
        state: {
          projectID: targetProjectId,
          priorityId: priorityItem.id,
          month: new Date().getMonth() + 1
        }
      });
    } else {
      // Generic management priority completion
      if (priorityItem.status === 'Completed') {
        toast.error('Already completed');
        return;
      }
      approveProjectSetupReviewMutation.mutate({
        id: priorityItem.id,
        payload: { status: 'Completed' }
      });
    }
  };

  const handleRejectInvoiceCancel = (id) => {
    const priorityItem = managementPrioritiesData?.data?.find(
      (item) => Number(item.id) === Number(id)
    );
    if (!priorityItem) {
      toast.error('Item not found');
      return;
    }
    if (priorityItem.status === 'Completed' || priorityItem.invoice?.cancelled) {
      toast.error('Already completed');
      return;
    }
    const invoiceId = priorityItem.invoice_id || priorityItem.invoice?.id;
    if (!invoiceId) {
      toast.error('Invoice ID not found');
      return;
    }
    rejectInvoiceCancelMutation.mutate({
      id: invoiceId,
      payload: { priority_id: priorityItem.id },
      priorityId: priorityItem.id
    });
  };

  // Handle note submission
  const onSubmitNote = (data) => {
    if (!selectedItem) return;

    setIsSubmitting(true);

    const payload = {
      notes: data.notes || ''
    };

    let updateMutation;
    let refetchFunction;

    if (escalationType === 'salesEscalations') {
      updateMutation = updateFollowUpMutation;
      refetchFunction = refetchSalesEscalations;
    } else if (escalationType === 'engineerEscalations') {
      updateMutation = updateEngineerTaskMutation;
      refetchFunction = refetchEngineerEscalations;
    } else if (escalationType === 'inventoryEscalations') {
      updateMutation = updateInventoryTaskMutation;
      refetchFunction = refetchInventoryEscalations;
    }

    if (updateMutation) {
      updateMutation.mutate(
        { id: selectedItem.id, payload },
        {
          onSuccess: () => {
            refetchFunction();
            toast.success('Note added successfully');
            setShowNoteModal(false);
            setSelectedItem(null);
            reset();
            setIsSubmitting(false);
          },
          onError: () => {
            setIsSubmitting(false);
          }
        }
      );
    } else {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    mixpanel.track('Priorities Page Viewed', {
      user_id: userID,
      escalation_type: escalationType,
      priority_type: priorityType,
      project_id: projectID,
      timestamp: new Date().toISOString()
    });
  }, [userID, escalationType, priorityType, projectID, selectedDate]);

  const isLoading =
    isLoadingSalesEscalations ||
    isLoadingEngineerEscalations ||
    isLoadingInventoryEscalations ||
    isLoadingManagementPriorities ||
    isLoadingCCRTasks ||
    isSubmitting ||
    updateFollowUpMutation.isPending ||
    updateEngineerTaskMutation.isPending ||
    updateInventoryTaskMutation.isPending ||
    approveUCMutation.isPending ||
    approveDealMutation.isPending ||
    updateCCRItemMutation.isPending;

  return (
    <Layout>
      <Loader isVisible={isLoading} />
      <div className="w-full h-full">
        <div
          className={`fixed top-0 z-10 transition-all duration-300 bg-white ${collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'
            }`}>
          <Header data={{ name: getPageTitle() }} backArrow={true} onBack={onBack} />
        </div>

        {/* Table - Custom tables for priority types, custom table for escalations */}
        <GridWrapper width={100}>
          <div className="mt-[6rem] mb-4">
            <AiSectionBanner projectId={projectID} vertical="management" surface="priorities" />
          </div>
          {showCCRItems ? (
            <div className="mt-[6rem] relative">
              <div className="w-full flex justify-between items-center mb-4">
                <div className="flex gap-1 justify-start items-center font-medium text-[18px]">
                  <div
                    onClick={() => {
                      setShowCCRItems(false);
                      setSelectedCCRId(null);
                      setSelectedPriorityId(null);
                    }}
                    className="flex justify-start items-center cursor-pointer">
                    <img height={30} width={30} src={LeftArrow} alt="back" />
                    Back
                    <img className="rotate-180" src={BackArrow} alt="forward" />
                  </div>
                  <span className="ml-2">Change Requests</span>
                </div>
              </div>
              <div className="w-full overflow-clip bg-gray-100">
                <div className="w-full relative rounded-t-lg mt-3">
                  <style>{`
                    .ccr-items-table-container {
                      height: auto !important;
                      min-height: fit-content !important;
                    }
                    .ccr-items-table-container .table-container {
                      height: auto !important;
                      min-height: fit-content !important;
                    }
                    .ccr-items-table-container .table-body {
                      position: static !important;
                      overflow-y: visible !important;
                      max-height: none !important;
                      height: auto !important;
                      min-height: fit-content !important;
                      top: auto !important;
                      padding-bottom: 0 !important;
                    }
                    .ccr-items-table-container table {
                      width: 100%;
                    }
                    .ccr-items-table-container th,
                    .ccr-items-table-container td {
                      padding: 1rem !important;

                    }
                    .ccr-items-table-container tbody tr {
                      height: auto !important;
                      min-height: 3.5rem !important;
                    }
                  `}</style>
                  <div className="ccr-items-table-container">
                    <Table
                      headers={[
                        'Requirements',
                        'Assigned to',
                        'Effort',
                        'Budget',
                        'Status',
                        'Engineer Approval',
                        'Builder Approval'
                      ]}
                      data={ccrItemsTableData}
                      identifier={'id'}
                      onYes={handleCCRYes}
                      onNo={handleCCRNo}
                      isDeleteEnable={false}
                      isEditEnable={false}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : priorityType === 'Invoice Cancel' ? (
            <div className="mt-[6rem] relative bg-white">
              <div className="table-container">
                <Table
                  headers={[
                    'Invoice Number',
                    'Customer',
                    'Unit Name',
                    'Project',
                    'Amount',
                    'Source',
                    'Status',
                    'Created At',
                    'Action'
                  ]}
                  data={tableData}
                  identifier={'id'}
                  onApprove={handleApprove}
                  onNo={handleRejectInvoiceCancel}
                  isDeleteEnable={false}
                  isEditEnable={false}
                />
              </div>
            </div>
          ) : priorityType === 'Approve UC' ? (
            <div className="mt-[6rem] relative bg-white">
              <div className="table-container">
                <style>{`
                  .table-container .table-body {
                    position: static !important;
                    overflow-y: visible !important;
                    max-height: none !important;
                    height: auto !important;
                    top: auto !important;
                    padding-bottom: 0 !important;
                  }
                  .table-container .table-container {
                    height: auto !important;
                  }
                  .table-container > div {
                    height: auto !important;
                  }
                `}</style>
                <Table
                  headers={[
                    'Lead Name',
                    'Unit Name',
                    'Project',
                    'Customer Proposal',
                    'Revised Plc Price',
                    'GST',
                    'Listed Rate',
                    'Initial Discount',
                    'Status',
                    'Created At',
                    'Action'
                  ]}
                  data={tableData}
                  identifier={'id'}
                  onApprove={handleApprove}
                  isDeleteEnable={false}
                  isEditEnable={false}
                />
              </div>
            </div>
          ) : priorityType === 'Deal Approval' ? (
            <div className="mt-[6rem] relative bg-white">
              <div className="w-full overflow-clip bg-gray-100">
                <div className="overflow-scroll hide-scrollbar w-full relative rounded-t-lg mt-3">
                  <table className="text-left w-full">
                    <thead
                      className="bg-gray-200 !text-gray-600 w-full"
                      style={{ position: 'sticky', top: 0, left: 0, zIndex: 1 }}>
                      <tr className="font-normal h-[3rem] w-full">
                        <th className="px-4 py-2 border-b font-normal">Lead Name</th>
                        <th className="px-4 py-2 border-b font-normal">Unit Name</th>
                        <th className="px-4 py-2 border-b font-normal">Project</th>
                        <th className="px-4 py-2 border-b font-normal">Customer Proposal</th>
                        <th className="px-4 py-2 border-b font-normal">Revised Plc Price</th>
                        <th className="px-4 py-2 border-b font-normal">GST</th>
                        <th className="px-4 py-2 border-b font-normal">Listed Rate</th>
                        <th className="px-4 py-2 border-b font-normal">Initial Discount</th>
                        <th className="px-4 py-2 border-b font-normal">Status</th>
                        <th className="px-4 py-2 border-b font-normal">Created At</th>
                        <th className="px-4 py-2 border-b font-normal">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableData && tableData.length > 0 ? (
                        tableData.map((row) => (
                          <tr
                            key={row.id}
                            className="border-b hover:bg-gray-50 bg-white h-[3.5rem] cursor-pointer"
                            onClick={() => {
                              if (row.lead_id) {
                                navigateWithReturn(navigate, `/builder/deals/${row.lead_id}`, {
                                  returnTo: buildReturnToFromLocation(location),
                                  state: { status: 'Closed Won' }
                                });
                              }
                            }}>
                            <td className="px-4 py-2 text-sm">{row.name || '---'}</td>
                            <td className="px-4 py-2 text-sm">{row.Unit_Name || '---'}</td>
                            <td className="px-4 py-2 text-sm">{row.project || '---'}</td>
                            <td className="px-4 py-2 text-sm">{row.Customer_Proposal || '---'}</td>
                            <td className="px-4 py-2 text-sm">{row.Plc_Price || '---'}</td>
                            <td className="px-4 py-2 text-sm">{row.GST || '---'}</td>
                            <td className="px-4 py-2 text-sm">{row.Listed_Rate || '---'}</td>
                            <td className="px-4 py-2 text-sm">{row.Customer_Discount || '---'}</td>
                            <td className="px-4 py-2 text-sm">{row.status || '---'}</td>
                            <td className="px-4 py-2 text-sm">
                              {row.created_at && row.created_at !== '---'
                                ? formatDate(row.created_at)
                                : '---'}
                            </td>
                            <td className="px-4 py-2 text-sm">
                              <div className="flex items-center justify-start w-full relative gap-3">
                                <Button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleApprove(row.id);
                                  }}
                                  disabled={
                                    row.status === 'Approved' ||
                                    approveDealMutation.isPending ||
                                    Number(approvingPriorityId) === Number(row.id)
                                  }>
                                  {row.status === 'Approved'
                                    ? 'Approved'
                                    : Number(approvingPriorityId) === Number(row.id)
                                      ? 'Approving…'
                                      : 'Approve'}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={11} className="text-center py-10 align-middle">
                            <div className="flex flex-col items-center justify-center w-full h-full">
                              <img
                                src={noDataIcon}
                                alt="No Data"
                                className="w-[25rem] h-[20rem] object-cover"
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : priorityType === 'Approve CCR' ? (
            <div className="mt-[6rem] relative bg-white">
              <div className="w-full overflow-clip bg-gray-100">
                <div className="overflow-scroll hide-scrollbar w-full relative rounded-t-lg mt-3">
                  <table className="text-left w-full">
                    <thead
                      className="bg-gray-200 !text-gray-600 w-full"
                      style={{ position: 'sticky', top: 0, left: 0, zIndex: 1 }}>
                      <tr className="font-normal h-[3rem] w-full">
                        <th className="px-4 py-2 border-b font-normal">Customer Name</th>
                        <th className="px-4 py-2 border-b font-normal">Unit/Block</th>
                        <th className="px-4 py-2 border-b font-normal">Project Name</th>
                        <th className="px-4 py-2 border-b font-normal">No of Requests</th>
                        <th className="px-4 py-2 border-b font-normal">Total Cost</th>
                        <th className="px-4 py-2 border-b font-normal">Total Effort</th>
                        <th className="px-4 py-2 border-b font-normal">Status</th>
                        <th className="px-4 py-2 border-b font-normal">Customer Approval</th>
                        <th className="px-4 py-2 border-b font-normal">Created At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableData && tableData.length > 0 ? (
                        tableData.map((row) => (
                          <tr
                            key={row.id}
                            className="border-b hover:bg-gray-50 bg-white h-[3.5rem] cursor-pointer"
                            onClick={() => {
                              const priorityItem = managementPrioritiesData?.data?.find(
                                (item) => Number(item.id) === Number(row.id)
                              );
                              if (priorityItem) {
                                handleCCRClick(priorityItem);
                              }
                            }}>
                            <td className="px-4 py-2 text-sm">{row.Customer_Name || '---'}</td>
                            <td className="px-4 py-2 text-sm">{row.Unit_Block || '---'}</td>
                            <td className="px-4 py-2 text-sm">{row.Project_Name || '---'}</td>
                            <td className="px-4 py-2 text-sm">{row.No_of_Requests || '---'}</td>
                            <td className="px-4 py-2 text-sm">
                              {row.Total_Cost
                                ? `₹${parseInt(row.Total_Cost).toLocaleString('en-IN')}`
                                : '---'}
                            </td>
                            <td className="px-4 py-2 text-sm">{row.Total_Effort || '---'}</td>
                            <td className="px-4 py-2 text-sm">{row.status || '---'}</td>
                            <td className="px-4 py-2 text-sm">{row.Customer_Approval || '---'}</td>
                            <td className="px-4 py-2 text-sm">
                              {row.created_at && row.created_at !== '---'
                                ? formatDate(row.created_at)
                                : '---'}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={9} className="text-center py-10 align-middle">
                            <div className="flex flex-col items-center justify-center w-full h-full">
                              <img
                                src={noDataIcon}
                                alt="No Data"
                                className="w-[25rem] h-[20rem] object-cover"
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : priorityType && priorityType !== 'File Request' ? (
            <div className="mt-[6rem] relative bg-white">
              <div className="table-container">
                <style>{`
                  .table-container .table-body {
                    position: static !important;
                    overflow-y: visible !important;
                    max-height: none !important;
                    height: auto !important;
                    top: auto !important;
                    padding-bottom: 0 !important;
                  }
                  .table-container .table-container {
                    height: auto !important;
                  }
                  .table-container > div {
                    height: auto !important;
                  }
                `}</style>
                <Table
                  headers={['Title', 'Description', 'Project', 'Status', 'Created At', 'Action']}
                  data={tableData}
                  identifier={'id'}
                  onApprove={handleApprove}
                  isDeleteEnable={false}
                  isEditEnable={false}
                />
              </div>
            </div>
          ) : (
            <div className="mt-[6rem] relative bg-white">
              <div className="overflow-scroll hide-scrollbar relative rounded-t-lg">
                <table className="text-left w-full">
                  <thead
                    className="bg-gray-200 !text-gray-600 w-full"
                    style={{ position: 'sticky', top: 0, left: 0, zIndex: 1 }}>
                    <tr className="font-normal h-[3rem] w-full">{getTableHeaders()}</tr>
                  </thead>
                  <tbody>
                    {tableData && tableData.length > 0 ? (
                      tableData.map((row) => (
                        <tr key={row.id} className="border-b hover:bg-gray-50 bg-white h-[3.5rem]">
                          {getTableRowCells(row)}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={
                            escalationType === 'salesEscalations'
                              ? 8
                              : escalationType === 'engineerEscalations'
                                ? 8
                                : 6
                          }
                          className="text-center py-7">
                          <div className="flex flex-col items-center justify-center">
                            <img
                              src={noDataIcon}
                              alt="No Data"
                              className="w-[25rem] h-[20rem] object-cover"
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </GridWrapper>

        {/* Add Note Modal */}
        {showNoteModal && (
          <Modal
            name="Add Note"
            width={50}
            onClose={() => {
              setShowNoteModal(false);
              setSelectedItem(null);
              reset();
            }}>
            <form onSubmit={handleSubmit(onSubmitNote)}>
              <div className="space-y-4">
                <FormInput
                  width={100}
                  label="Notes"
                  register={register}
                  register_key="notes"
                  type="textarea"
                  isRequired={true}
                  validation={{ required: 'Notes are required' }}
                  errors={errors}
                  placeholder="Enter notes here..."
                />

                {/* Status field - hidden but set automatically based on escalation type */}
                <input
                  type="hidden"
                  {...register('status', { required: true })}
                  value={escalationType === 'salesEscalations' ? 'Inactive' : 'Completed'}
                />
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <Button
                  type="button"
                  displayType="secondary"
                  onClick={() => {
                    setShowNoteModal(false);
                    setSelectedItem(null);
                    reset();
                  }}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
              </div>
            </form>
          </Modal>
        )}
      </div>
    </Layout>
  );
};

export default Priorities;
